import React from 'react';
import PropTypes from 'prop-types';
import ListItem from './ListItem';
import { refSetter } from '../../../utils/helpers';
import globals from '../../../globals/communityGlobals';

const locale = globals.getArea('selectableListsValues');

class SelectableList extends React.Component {
  constructor(props) {
    super(props);
    if (props.multiselect && props.toggleOnClick) {
      throw new Error('SelectableList cannot be multiselect and toggle on click at the same time');
    }
    this.state = {
      shouldUpdateLabel: false,
      selectedOptions: props.selectedOptions,
    };

    this.dropdownListStyle = { maxHeight: `${this.props.maxHeight}px` };
  }

  componentDidMount = () => {
    this.updateLabel();
  };

  componentDidUpdate = prevProps => {
    const selectedSource = this.props.externalState ? this.props : this.state;
    this.selected = this.mapSelected(selectedSource, this.props);
    const { selectedOptions } = this.props;
    if (
      this.state.shouldUpdateLabel ||
      (selectedOptions && selectedOptions[0].index !== prevProps.selectedOptions[0].index)
    ) {
      this.updateLabel();
    }
  };

  setItemRef = refSetter.bind(this);

  selectItem = index => {
    const { multiselect, toggleOnClick, close, externalState, externalCallback } = this.props;
    const stateSource = externalState ? this.props : this.state;
    const newState = { ...stateSource };
    const { selectedOptions } = newState;
    if (multiselect) {
      const existingItemIndex = selectedOptions.findIndex(el => el.index === index);
      if (existingItemIndex >= 0) {
        selectedOptions.splice(existingItemIndex, 1);
      } else {
        newState.selectedOptions = [...selectedOptions, { index }];
      }
      newState.selectedOptions.sort((a, b) => a.index - b.index);
    } else if (toggleOnClick) {
      if (selectedOptions.length === 0 || index === selectedOptions[0].index) {
        newState.selectedOptions[0] = {
          index,
          clickedBefore: selectedOptions[0] ? !selectedOptions[0].clickedBefore : false,
        };
      } else {
        newState.selectedOptions[0] = {
          index,
          clickedBefore: false,
        };
      }
    } else {
      newState.selectedOptions = [{ index }];
    }
    if (externalState) {
      externalCallback(this.mapSelected(newState, this.props));
      this.setState({ shouldUpdateLabel: true });
    } else {
      this.setState({ ...newState, shouldUpdateLabel: true });
    }
    if (!multiselect) close();
  };

  updateLabel = () => {
    const { multiselect, setLabel, placeholder, externalState } = this.props;
    const { selectedOptions } = externalState ? this.props : this.state;
    let label;
    if (selectedOptions.length === 0) {
      label = <span className="placeholder">{placeholder}</span>;
    } else if (!multiselect) {
      label = this[`item${selectedOptions[0].index}`] ? (
        this[`item${selectedOptions[0].index}`].childElements
      ) : (
        <span className="placeholder">{placeholder}</span>
      );
    } else {
      label = (
        <span>{this.selected.reduce((sum, value, index) => (index === 0 ? value : `${sum}, ${value}`), '')}</span>
      );
    }
    setLabel(label);
    this.setState({ shouldUpdateLabel: false });
  };

  mapSelected = (source, optionsSource) =>
    source.selectedOptions.map(({ index, clickedBefore = false }) => {
      const rest =
        typeof optionsSource.options[index] === 'string'
          ? { name: optionsSource.options[index] }
          : optionsSource.options[index];
      return {
        index,
        clickedBefore,
        ...rest,
      };
    });

  render() {
    const { options, translate, itemShape, iconCss, toggleOnClick, identifier, externalState } = this.props;
    const { selectedOptions } = externalState ? this.props : this.state;
    const itemChildren = itemShape.split('+');
    const itemsList = options.map((el, index) => {
      const name = typeof el === 'string' ? el : el.name;
      return (
        <ListItem
          // eslint-disable-next-line react/no-array-index-key
          key={`${identifier}ListItem${index}`}
          ref={this.setItemRef(`item${index}`)}
          index={index}
          name={translate ? locale.getMsg(identifier + name) : name}
          select={this.selectItem}
          itemChildren={itemChildren}
          identifier={identifier}
        />
      );
    });
    selectedOptions.forEach(({ index, clickedBefore = false }) => {
      if (itemsList[index]) {
        itemsList[index] = React.cloneElement(itemsList[index], {
          ...itemsList[index].props,
          isSelected: true,
          iconClass: toggleOnClick ? iconCss[Number(clickedBefore)] : iconCss,
        });
      }
    });
    return (
      <ul className="dropdownList" style={this.dropdownListStyle}>
        {itemsList}
      </ul>
    );
  }
}

SelectableList.defaultProps = {
  setLabel: () => {},
  close: () => {},
  translate: false,
  itemShape: 'name',
  selectedOptions: [],
  multiselect: false,
  toggleOnClick: false,
  iconCss: '',
  placeholder: locale.getMsg('defaultPlaceholder'),
  externalState: false,
  externalCallback: null,
  maxHeight: null,
};

SelectableList.propTypes = {
  // Whether the list should work with its own state in isolation,
  // or use redux/parent state with callbacks
  externalState: PropTypes.bool,
  // The function called whenever the user selects an item
  externalCallback: PropTypes.func,
  // Whether the list items should toggle on click
  // (i.e. choose a new item: default state, click the same item again: toggled state)
  toggleOnClick: PropTypes.bool,
  // Whether the list should support multiple items selected.
  multiselect: PropTypes.bool,
  // A unique string identifying this specific list, used for translations and key generation
  identifier: PropTypes.string.isRequired,
  // The options available for selection.
  // Can be an array of strings or an array of objects with name property defined.
  options: PropTypes.arrayOf(
    PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.shape({
        name: PropTypes.string.isRequired,
      }),
    ]),
  ).isRequired,
  // An array of the currently selected options,
  // clickedBefore is used for toggling and defautls to false if not provided
  selectedOptions: PropTypes.arrayOf(
    PropTypes.shape({
      index: PropTypes.number,
      clickedBefore: PropTypes.bool,
    }),
  ),
  // Placeholder message
  placeholder: PropTypes.string,
  // Describes what the label should look like.
  // Valid options: 'name', 'icon', 'name+icon','icon+name'
  itemShape: PropTypes.string,
  // Whether the list items should be translated.
  // Will check for translations under `identifier + item name`
  translate: PropTypes.bool,
  // Strings describing the css classes of icons used (if there are any, check itemShape)
  // Can be a single string if it's a regular list or an array of 2 elements for toggleOnClick lists
  // first class = default state icon, second class = toggled state icon
  iconCss: PropTypes.oneOfType([PropTypes.arrayOf(PropTypes.string), PropTypes.string]),
  // Max height of the list in pixels
  maxHeight: PropTypes.number,
  // Function used to set the label of the dropdown wrapper (provided by DropdownWrapper)
  setLabel: PropTypes.func,
  // Function used to close the dropdown upon selection (provided by DropdownWrapper)
  close: PropTypes.func,
};

export default SelectableList;
